/**
 * SEO Optimized Landing Page Component
 * Carbon Design System compliant landing page with SEO best practices
 */

import React, { useEffect, useState } from 'react';
import { Grid, Column, Button, Tile, ClickableTile, Loading, Link, Tag, AspectRatio } from '@carbon/react';
import {
  ArrowRight,
  PlayFilled,
  ChevronRight,
  Analytics,
  Education,
  Growth,
  UserFollow,
  Globe,
} from '@carbon/react/icons';
import SEOLayout from './SEOLayout';

interface LandingPageContent {
  hero: {
    headline: string;
    subheadline: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  features: {
    title: string;
    items: Array<{
      title: string;
      description: string;
      icon?: string;
    }>;
  };
  testimonials?: Array<{
    quote: string;
    author: string;
    title: string;
    company: string;
  }>;
  stats?: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
}

interface SEOOptimizedLandingPageProps {
  language?: string;
  content?: LandingPageContent;
  className?: string;
}

export const SEOOptimizedLandingPage: React.FC<SEOOptimizedLandingPageProps> = ({
  language = 'en',
  content,
  className = '',
}) => {
  const [pageContent, setPageContent] = useState<LandingPageContent | null>(content || null);
  const [isLoading, setIsLoading] = useState(!content);

  useEffect(() => {
    if (!content) {
      loadPageContent();
    }
  }, [language, content]);

  const loadPageContent = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/i18n/content/${language}?page=landing`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.content) {
          setPageContent(result.data.content);
        }
      }
    } catch (error) {
      console.error('Failed to load page content:', error);
      // Fallback content
      setPageContent(getDefaultContent(language));
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultContent = (lang: string): LandingPageContent => {
    const isPortuguese = lang === 'pt' || lang === 'pt-BR';

    return {
      hero: {
        headline: isPortuguese ? 'Transforme a Educação com IA' : 'Transform Education with AI',
        subheadline: isPortuguese
          ? 'Melhore os resultados dos alunos, aumente a produtividade dos professores e alcance ROI mensurável com nossa plataforma educacional abrangente.'
          : 'Improve student outcomes, enhance teacher productivity, and achieve measurable ROI with our comprehensive educational platform.',
        ctaPrimary: isPortuguese ? 'Iniciar Teste Grátis' : 'Start Free Trial',
        ctaSecondary: isPortuguese ? 'Solicitar Demo' : 'Request Demo',
      },
      features: {
        title: isPortuguese ? 'Soluções Educacionais Completas' : 'Comprehensive Educational Solutions',
        items: [
          {
            title: isPortuguese ? 'Tutoria com IA' : 'AI-Powered Tutoring',
            description: isPortuguese
              ? 'Experiências de aprendizado personalizadas adaptadas às necessidades de cada aluno.'
              : "Personalized learning experiences adapted to each student's needs and learning style.",
          },
          {
            title: isPortuguese ? 'Correção Automatizada' : 'Automated Grading',
            description: isPortuguese
              ? 'Economize horas do tempo do professor com correção inteligente e feedback instantâneo.'
              : 'Save hours of teacher time with intelligent grading and instant feedback.',
          },
          {
            title: isPortuguese ? 'Analytics de Progresso' : 'Progress Analytics',
            description: isPortuguese
              ? 'Acompanhe o progresso dos alunos com analytics detalhados e insights acionáveis.'
              : 'Track student progress with detailed analytics and actionable insights.',
          },
        ],
      },
      stats: [
        {
          value: '95%',
          label: isPortuguese ? 'Melhoria no Desempenho' : 'Performance Improvement',
          description: isPortuguese ? 'dos estudantes' : 'in student outcomes',
        },
        {
          value: '40%',
          label: isPortuguese ? 'Economia de Tempo' : 'Time Savings',
          description: isPortuguese ? 'para professores' : 'for teachers',
        },
        {
          value: '300%',
          label: 'ROI',
          description: isPortuguese ? 'em 12 meses' : 'within 12 months',
        },
      ],
    };
  };

  const getIconComponent = (iconName?: string) => {
    switch (iconName) {
      case 'analytics':
        return <Analytics size={32} />;
      case 'education':
        return <Education size={32} />;
      case 'growth':
        return <Growth size={32} />;
      case 'user':
        return <UserFollow size={32} />;
      case 'globe':
        return <Globe size={32} />;
      case 'award':
        return <Education size={32} />;
      default:
        return <Education size={32} />;
    }
  };

  if (isLoading) {
    return (
      <SEOLayout page="landing" language={language}>
        <div className="landing-page--loading">
          <Loading description={language === 'pt' ? 'Carregando...' : 'Loading...'} />
        </div>
      </SEOLayout>
    );
  }

  if (!pageContent) {
    return (
      <SEOLayout page="landing" language={language}>
        <div className="landing-page--error">
          <h1>{language === 'pt' ? 'Erro ao carregar conteúdo' : 'Error loading content'}</h1>
        </div>
      </SEOLayout>
    );
  }

  const breadcrumbs = [{ href: '/', text: language === 'pt' ? 'Início' : 'Home' }];

  return (
    <SEOLayout page="landing" language={language} breadcrumbs={breadcrumbs} className={`landing-page ${className}`}>
      {/* Hero Section */}
      <section className="landing-page__hero" role="banner">
        <Grid fullWidth>
          <Column lg={10} md={6} sm={4}>
            <div className="landing-page__hero-content">
              <h1 className="landing-page__hero-headline">{pageContent.hero.headline}</h1>
              <p className="landing-page__hero-subheadline">{pageContent.hero.subheadline}</p>
              <div className="landing-page__hero-actions">
                <Button
                  kind="primary"
                  size="lg"
                  renderIcon={ArrowRight}
                  href="/trial-signup"
                  className="landing-page__cta-primary"
                >
                  {pageContent.hero.ctaPrimary}
                </Button>
                <Button
                  kind="secondary"
                  size="lg"
                  renderIcon={PlayFilled}
                  href="/demo-request"
                  className="landing-page__cta-secondary"
                >
                  {pageContent.hero.ctaSecondary}
                </Button>
              </div>
            </div>
          </Column>
          <Column lg={6} md={2} sm={0}>
            <div className="landing-page__hero-media">
              <AspectRatio ratio="16x9">
                <img
                  src="/images/hero/sigma08-platform-demo.jpg"
                  alt={language === 'pt' ? 'Demonstração da Plataforma Sigma08' : 'Sigma08 Platform Demo'}
                  loading="eager"
                  width="800"
                  height="450"
                />
              </AspectRatio>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Statistics Section */}
      {pageContent.stats && (
        <section className="landing-page__stats" aria-labelledby="stats-heading">
          <Grid fullWidth>
            <Column lg={16} md={8} sm={4}>
              <h2 id="stats-heading" className="visually-hidden">
                {language === 'pt' ? 'Estatísticas de Impacto' : 'Impact Statistics'}
              </h2>
              <div className="landing-page__stats-grid">
                {pageContent.stats.map((stat, index) => (
                  <Tile key={index} className="landing-page__stat-tile">
                    <div className="landing-page__stat-value">{stat.value}</div>
                    <div className="landing-page__stat-label">{stat.label}</div>
                    {stat.description && <div className="landing-page__stat-description">{stat.description}</div>}
                  </Tile>
                ))}
              </div>
            </Column>
          </Grid>
        </section>
      )}

      {/* Features Section */}
      <section className="landing-page__features" aria-labelledby="features-heading">
        <Grid fullWidth>
          <Column lg={16} md={8} sm={4}>
            <div className="landing-page__features-header" style={{ marginBottom: 'var(--cds-spacing-06)' }}>
              <h2
                id="features-heading"
                style={{
                  fontSize: 'var(--cds-productive-heading-05-font-size)',
                  fontWeight: 'var(--cds-productive-heading-05-font-weight)',
                  marginBottom: 'var(--cds-spacing-05)',
                }}
              >
                {pageContent.features.title}
              </h2>
            </div>
            <Grid narrow>
              {pageContent.features.items.map((feature, index) => (
                <Column key={index} lg={5} md={4} sm={4} style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                  <ClickableTile
                    href="/features"
                    style={{
                      height: '100%',
                      padding: 'var(--cds-spacing-05)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--cds-spacing-04)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 'var(--cds-spacing-03)',
                      }}
                    >
                      {getIconComponent(feature.icon)}
                    </div>
                    <h3
                      style={{
                        fontSize: 'var(--cds-productive-heading-03-font-size)',
                        fontWeight: 'var(--cds-productive-heading-03-font-weight)',
                        color: 'var(--cds-text-primary)',
                        marginBottom: 'var(--cds-spacing-03)',
                      }}
                    >
                      {feature.title}
                    </h3>
                    <p
                      style={{
                        color: 'var(--cds-text-secondary)',
                        fontSize: 'var(--cds-body-01-font-size)',
                        lineHeight: 'var(--cds-body-01-line-height)',
                        flexGrow: 1,
                      }}
                    >
                      {feature.description}
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--cds-spacing-02)',
                        marginTop: 'var(--cds-spacing-03)',
                        color: 'var(--cds-link-primary)',
                        fontSize: 'var(--cds-helper-text-01-font-size)',
                      }}
                    >
                      {language === 'pt' ? 'Saiba mais' : 'Learn more'}
                      <ChevronRight size={16} />
                    </div>
                  </ClickableTile>
                </Column>
              ))}
            </Grid>
          </Column>
        </Grid>
      </section>

      {/* Testimonials Section */}
      {pageContent.testimonials && (
        <section className="landing-page__testimonials" aria-labelledby="testimonials-heading">
          <Grid fullWidth>
            <Column lg={16} md={8} sm={4}>
              <h2 id="testimonials-heading">
                {language === 'pt' ? 'O que nossos clientes dizem' : 'What our customers say'}
              </h2>
              <div className="landing-page__testimonials-grid">
                {pageContent.testimonials.map((testimonial, index) => (
                  <Tile key={index} className="landing-page__testimonial">
                    <blockquote>
                      <p>"{testimonial.quote}"</p>
                      <footer>
                        <cite>
                          <strong>{testimonial.author}</strong>
                          <br />
                          {testimonial.title}, {testimonial.company}
                        </cite>
                      </footer>
                    </blockquote>
                  </Tile>
                ))}
              </div>
            </Column>
          </Grid>
        </section>
      )}

      {/* Call to Action Section */}
      <section className="landing-page__cta" role="complementary">
        <Grid fullWidth>
          <Column lg={12} md={6} sm={4} lgOffset={2} mdOffset={1}>
            <div className="landing-page__cta-content">
              <h2>
                {language === 'pt' ? 'Pronto para transformar sua educação?' : 'Ready to transform your education?'}
              </h2>
              <p>
                {language === 'pt'
                  ? 'Junte-se a milhares de educadores que já estão usando Sigma08 para melhorar os resultados dos alunos.'
                  : 'Join thousands of educators already using Sigma08 to improve student outcomes.'}
              </p>
              <div className="landing-page__cta-actions">
                <Button kind="primary" size="lg" renderIcon={ArrowRight} href="/trial-signup">
                  {pageContent.hero.ctaPrimary}
                </Button>
                <Link href="/contact">{language === 'pt' ? 'Fale conosco' : 'Contact us'}</Link>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* SEO-friendly footer content */}
      <footer className="landing-page__footer" role="contentinfo">
        <Grid fullWidth>
          <Column lg={16} md={8} sm={4}>
            <div className="landing-page__footer-content">
              <p>
                {language === 'pt'
                  ? '© 2024 Sigma08. Plataforma educacional com IA para excelência K-12.'
                  : '© 2024 Sigma08. AI-powered educational platform for K-12 excellence.'}
              </p>
              <div className="landing-page__footer-tags">
                <Tag type="blue">{language === 'pt' ? 'Tecnologia Educacional' : 'Educational Technology'}</Tag>
                <Tag type="green">{language === 'pt' ? 'IA para Educação' : 'AI for Education'}</Tag>
                <Tag type="purple">{language === 'pt' ? 'Plataforma K-12' : 'K-12 Platform'}</Tag>
              </div>
            </div>
          </Column>
        </Grid>
      </footer>
    </SEOLayout>
  );
};

export default SEOOptimizedLandingPage;
